import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

const STATUS_OPTIONS = ["planned", "active", "stopped", "abandoned"];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function formatIST(value) {
  if (!value) return "";

  const raw = String(value).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (!match) return raw;

  const [, yyyy, mm, dd, hh, min, ss = "00"] = match;

  let hour = parseInt(hh, 10);
  const ampm = hour >= 12 ? "PM" : "AM";

  hour = hour % 12;
  if (hour === 0) hour = 12;

  return `${dd}-${mm}-${yyyy} ${String(hour).padStart(
    2,
    "0"
  )}:${min}:${ss} ${ampm}`;
}

function valueOrDash(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

export default function SentinelEscortList() {
  const skin = useSkin();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const recordsPerPage = 10;

  const routePage = useMemo(() => "/sentinel_add_Route", []);

  const sentinelEscortPage = useMemo(() => "/sentinel_add_escort", []);

  const sentinelTrackingPage = "/sentinel_escort_live_tracking";

  const authHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const buildQueryString = (
    nextPage = page,
    nextQ = q,
    nextStatus = status
  ) => {
    const search = new URLSearchParams();

    search.set("page", String(nextPage));
    search.set("limit", String(recordsPerPage));

    if (nextQ.trim()) search.set("q", nextQ.trim());
    if (nextStatus.trim()) search.set("status", nextStatus.trim());

    return search.toString();
  };

  const updateUrl = (nextPage = page, nextQ = q, nextStatus = status) => {
    const query = buildQueryString(nextPage, nextQ, nextStatus);
    navigate(`/sentinel_escort_list?${query}`, { replace: false });
  };

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    setQ(params.get("q") || "");
    setStatus(params.get("status") || "");
    setPage(Number(params.get("page") || 1));
  }, [params]);

  useEffect(() => {
    if (!loading) {
      fetchSentinelEscortRuns();
    }
  }, [location.search]);

  async function fetchPageData(local, token) {
    try {
      setLoading(true);

      const dashboardRes = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!dashboardRes.ok) {
        throw new Error("Failed to fetch dashboard");
      }

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      const mergedDashboard = {
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      };

      setDashboardData(mergedDashboard);

      await fetchSentinelEscortRuns();
    } catch (error) {
      console.error("Sentinel escort list page error:", error);
      toast.error(error.message || "Failed to load sentinel escort list.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchSentinelEscortRuns() {
    try {
      setTableLoading(true);

      const currentParams = new URLSearchParams(window.location.search);
      const currentPage = Number(currentParams.get("page") || page || 1);
      const currentQ = currentParams.get("q") ?? q;
      const currentStatus = currentParams.get("status") ?? status;

      const query = buildQueryString(currentPage, currentQ, currentStatus);

      const res = await fetch(`${API_BASE_URL}/sentinel_escort/runs?${query}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch sentinel escort runs");
      }

      const apiRows = Array.isArray(data.data)
        ? data.data
        : Array.isArray(data.rows)
        ? data.rows
        : Array.isArray(data.data?.runs)
        ? data.data.runs
        : [];

      setRows(apiRows);
      setTotalRecords(Number(data.totalRecords || data.total_records || 0));
      setTotalPages(Number(data.totalPages || data.total_pages || 1));
    } catch (err) {
      console.error("Fetch sentinel escort runs error:", err);

      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);

      toast.error(err.message || "Something went wrong");
    } finally {
      setTableLoading(false);
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    updateUrl(1, q, status);
  };

  const handleClear = () => {
    setQ("");
    setStatus("");
    setPage(1);
    navigate("/sentinel_escort_list");
    toast.success("Search cleared");
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
    updateUrl(nextPage, q, status);
  };

  const handleDelete = async (runId) => {
    if (!window.confirm(
      `Delete sentinel escort run #${runId}? This will remove the run and all its points/events.`
    )) return;

    try {
      const res = await fetch(`${API_BASE_URL}/sentinel_escort/runs/${runId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Delete failed");
      }

      toast.success(`Sentinel escort run #${runId} and its tracking data were deleted.`);
      fetchSentinelEscortRuns();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    if (page > 1) {
      items.push(
        <PageButton key="prev" onClick={() => handlePageChange(page - 1)}>
          ‹ Prev
        </PageButton>
      );
    }

    if (start > 1) {
      items.push(
        <PageButton key={1} onClick={() => handlePageChange(1)}>
          1
        </PageButton>
      );

      if (start > 2) {
        items.push(
          <span key="dots-start" className="text-[var(--text-muted)] px-1">
            …
          </span>
        );
      }
    }

    for (let i = start; i <= end; i += 1) {
      items.push(
        <PageButton
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageButton>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(
          <span key="dots-end" className="text-[var(--text-muted)] px-1">
            …
          </span>
        );
      }

      items.push(
        <PageButton key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </PageButton>
      );
    }

    if (page < totalPages) {
      items.push(
        <PageButton key="next" onClick={() => handlePageChange(page + 1)}>
          Next ›
        </PageButton>
      );
    }

    return (
      <div className="flex justify-center items-center gap-2 mt-5 flex-wrap">
        {items}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel escort runs...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel_escort"
      activeMenu="sentinel_escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel Escort Runs
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Manage sentinel escort runs, tracking points, status and live map view.
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Sentinel Escort Route List
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Search, view, edit, track, and delete sentinel escort run records.
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
                onClick={() => navigate(sentinelEscortPage)}
              >
                New Sentinel Escort
              </button>
              <button
                type="button"
                onClick={() => navigate(routePage)}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Create Route
              </button>
              <button
                type="button"
                onClick={() => navigate("/sentinel_route_list")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Route List
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Escorts
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {totalRecords}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Current Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {page} / {totalPages}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Per Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {recordsPerPage}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Status
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {rows.length > 0 ? "Active" : "Empty"}
            </p>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <form onSubmit={handleSearchSubmit} className="flex gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Search guard, title, from, to..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 min-w-[260px] rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </option>
              ))}
            </select>

            <button
              type="submit"
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Search
            </button>

            {(q || status) && (
              <button
                type="button"
                onClick={handleClear}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Escort Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-5 py-[18px] border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0">
              Sentinel Escort Database
            </p>
            <span className="text-[12px] text-[var(--text-muted)]">
              {totalRecords} entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            {tableLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
              </div>
            ) : rows.length > 0 ? (
              <>
                <table className="w-full min-w-[1100px] border-collapse">
                  <thead>
                    <tr>
                      <Th>#</Th>
                      <Th>Run / Title</Th>
                      <Th>Guard</Th>
                      <Th>From → To</Th>
                      <Th>Status</Th>
                      <Th>Start / End</Th>
                      <Th>Points</Th>
                      <Th>Distance</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row, index) => {
                      const runId =
                        row.run_id ||
                        row.sentinel_run_id ||
                        row.escort_run_id ||
                        row.id;

                      const badge = String(row.status || "unknown").toLowerCase();
                      const serial = (page - 1) * recordsPerPage + index + 1;

                      const memberId =
                        row.member_id || row.guard_id || row.guard_member_id || "";

                      return (
                        <tr key={runId || index} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                          <Td>
                            <span className="text-[var(--accent)] font-extrabold">
                              {serial}
                            </span>
                          </Td>

                          <Td>
                            <div className="font-extrabold text-[var(--text-primary)]">
                              #{valueOrDash(runId)}
                            </div>
                            <div className="text-[12px] text-[var(--text-muted)] mt-0.5">
                              {valueOrDash(row.title)}
                            </div>
                          </Td>

                          <Td>
                            <div className="flex items-center gap-2.5">
                              <div className="w-[38px] h-[38px] rounded-xl bg-[var(--btn-bg)] text-[var(--btn-text)] flex items-center justify-center text-[14px] font-extrabold flex-shrink-0 shadow-[0_3px_12px_var(--accent-glow)]">
                                {String(row.guard_name || row.username || "U").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="text-[13px] font-extrabold text-[var(--text-primary)]">
                                  {row.guard_name || row.username || valueOrDash(memberId)}
                                </div>
                                <div className="text-[11px] text-[var(--text-muted)] font-mono">
                                  {valueOrDash(memberId)}
                                </div>
                              </div>
                            </div>
                          </Td>

                          <Td>
                            <div className="flex items-center gap-1 text-[13px] text-[var(--text-primary)]">
                              <span className="text-[var(--accent)]">📍</span>
                              {valueOrDash(row.from_name)}
                            </div>
                            <div className="flex items-center gap-1 text-[13px] text-[var(--text-muted)] mt-0.5">
                              <span className="text-[#22c55e]">📍</span>
                              {valueOrDash(row.to_name)}
                            </div>
                          </Td>

                          <Td>
                            <StatusBadge status={badge} />
                          </Td>

                          <Td>
                            <div className="text-[12px] text-[var(--text-muted)] font-mono">
                              {formatIST(row.start_time) || "-"}
                            </div>
                            <div className="text-[12px] text-[var(--text-muted)] font-mono">
                              {formatIST(row.end_time) || "-"}
                            </div>
                          </Td>

                          <Td>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                              {Number(row.points_count || 0)}
                            </span>
                          </Td>

                          <Td>
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-primary)]">
                              {Number(row.distance_m || row.total_distance || 0)}m
                            </span>
                          </Td>

                          <Td>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`${sentinelEscortPage}?edit_id=${runId}`)
                                }
                                className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                                style={{
                                  background: "var(--accent)",
                                  color: "white",
                                }}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `${sentinelTrackingPage}?member_id=${encodeURIComponent(
                                      memberId || ""
                                    )}&run_id=${runId}`
                                  )
                                }
                                className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                                style={{
                                  background: "#22c55e",
                                  color: "white",
                                }}
                              >
                                Track
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDelete(runId)}
                                className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                                style={{
                                  background: "var(--red)",
                                  color: "white",
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {renderPagination()}
              </>
            ) : (
              <div className="py-12 px-5 text-center">
                <div className="text-5xl mb-3 text-[var(--text-faint)]">📋</div>
                <h3 className="text-[20px] font-extrabold text-[var(--text-primary)] m-0 mb-2">
                  No Sentinel Escort Runs Found
                </h3>
                <p className="text-[13px] text-[var(--text-muted)] m-0">
                  {q || status ? "No sentinel escorts match your current filters." : "Start by creating a new sentinel escort run."}
                </p>
                {!q && !status && (
                  <button
                    type="button"
                    onClick={() => navigate(sentinelEscortPage)}
                    className="mt-4 border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                    style={{
                      background: "var(--btn-bg)",
                      color: "var(--btn-text)",
                      boxShadow: "0 4px 18px var(--accent-glow)",
                    }}
                  >
                    New Sentinel Escort Run
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Th({ children }) {
  return (
    <th
      className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)] whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] border-b border-[var(--divider)] align-middle"
    >
      {children}
    </td>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "active"
      ? "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]"
      : status === "planned"
      ? "bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]"
      : status === "stopped"
      ? "bg-[rgba(245,158,11,0.12)] border border-[rgba(245,158,11,0.28)] text-[#f59e0b]"
      : status === "abandoned"
      ? "bg-[var(--red-faint)] border border-[var(--red-border)] text-[var(--red)]"
      : "bg-[var(--surface)] border border-[var(--surface-border)] text-[var(--text-muted)]";

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-extrabold ${cls}`}>
      ● {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function PageButton({ children, active = false, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`min-w-9 h-9 px-3 rounded-xl border text-[13px] font-extrabold transition-colors ${
        active
          ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
          : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
      }`}
    >
      {children}
    </button>
  );
}